import { Check, ChevronLeft, ChevronRight, CircleHelp, ImageOff, Minus, Pencil, RefreshCw, Save, Star, Trash2, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'

import { Button, ModalFrame, StatusMessage } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { PhotoCropReview } from '../../common-photos/ui/PhotoCropReview'
import type { AddPhotoVm, PairVm, PhotoRegistryActions, PhotoRegistryVm, PhotoTileVm, SendProgressVm, SlotVm } from '../model/types'
import './photoRegistry.css'

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif'

/** Картинка с запасным значком и словами: кадр не загрузился — это видно не только по цвету. */
function Frame({ url, alt }: { url: string | null; alt: string }) {
  const [broken, setBroken] = useState(false)
  if (url === null || broken) {
    return (
      <div className="platform-photo-registry__noimage" role="img" aria-label={`${alt}: превью недоступно`}>
        <ImageOff size={28} aria-hidden="true" />
        <span>Нет превью</span>
      </div>
    )
  }
  return <img src={url} alt={alt} draggable={false} loading="lazy" onError={() => setBroken(true)} />
}

/** Отметка источника под фотографией: значок и слово, цвет только поддерживает. */
function Mark({ slot }: { slot: SlotVm }) {
  if (slot.state === 'present') {
    return (
      <span className="platform-photo-registry__mark" data-state="present">
        <Check size={14} aria-hidden="true" /> {slot.title}: есть
      </span>
    )
  }
  if (slot.state === 'unknown') {
    return (
      <span className="platform-photo-registry__mark" data-state="unknown">
        <CircleHelp size={14} aria-hidden="true" /> {slot.title}: неизвестно
      </span>
    )
  }
  return (
    <span className="platform-photo-registry__mark" data-state="absent">
      <Minus size={14} aria-hidden="true" /> {slot.title}: нет
    </span>
  )
}

/** Что человек сейчас подтверждает: удаление временной загрузки или фото из источника. */
type DeleteConfirm = { kind: 'staging'; tile: PhotoTileVm } | { kind: 'source'; tile: PhotoTileVm; slot: SlotVm }

/**
 * Ход удаления словами. Отказ источника и «исход неизвестен» — дословно, с кодом и копированием, не пропадают сами; «Повторить» посылает то же
 * удаление с тем же ключом операции (второго удаления не будет), «Закрыть» убирает сообщение.
 */
function DeleteProgress({ progress, onRetry, onDismiss }: { progress: SendProgressVm; onRetry: () => void; onDismiss: () => void }) {
  const dismiss = (
    <Button variant="ghost" onClick={onDismiss} actionId="platform.photo-registry.delete-dismiss">
      Закрыть
    </Button>
  )
  if (progress.error !== null) {
    return (
      <StatusMessage
        kind="error"
        title={progress.text}
        text={progress.error.message}
        meta={`Код: ${progress.error.code}${progress.error.requestId === null ? '' : ` · запрос ${progress.error.requestId}`}`}
        action={
          <span className="platform-photo-registry__row">
            <Button onClick={onRetry} actionId="platform.photo-registry.delete-retry">
              Повторить
            </Button>
            {dismiss}
          </span>
        }
      />
    )
  }
  return (
    <>
      <p className="platform-photo-registry__meta" role="status">
        {progress.text}
      </p>
      {(progress.phase === 'done' || progress.phase === 'unchanged') && dismiss}
    </>
  )
}

/** «Добавить фото»: подготовка, правка рамки, предпросмотр и сохранение. Пока не нажато «Сохранить», на сервере ничего не лежит. */
function AddPhoto({ adding, actions }: { adding: AddPhotoVm; actions: PhotoRegistryActions }) {
  const saving = adding.phase === 'saving'
  return (
    <section className="platform-photo-registry__add" aria-label="Добавление фото">
      <h4>Новое фото: {adding.fileName}</h4>
      {adding.phase === 'preparing' && (
        <StatusMessage
          kind="loading"
          title={adding.stage}
          action={
            <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={actions.cancelAdd} actionId="platform.photo-registry.add-cancel">
              Отменить
            </Button>
          }
        />
      )}
      {adding.phase === 'failed' && (
        <>
          <ActionError error={adding.error} />
          <Button icon={<X size={16} aria-hidden="true" />} onClick={actions.cancelAdd} actionId="platform.photo-registry.add-cancel">
            Закрыть
          </Button>
        </>
      )}
      {(adding.phase === 'review' || adding.phase === 'previewing') && adding.review !== null && (
        <>
          <ActionError error={adding.error} />
          <PhotoCropReview
            review={adding.review}
            onAccept={actions.previewBox}
            onCancel={actions.cancelAdd}
            busy={adding.phase === 'previewing'}
            busyText={adding.phase === 'previewing' ? adding.stage : null}
            acceptKind="show-result"
            {...(adding.box !== null ? { startBox: adding.box } : {})}
          />
        </>
      )}
      {(adding.phase === 'preview' || saving) && (
        <>
          <ActionError error={adding.error} />
          <p className="platform-photo-registry__meta">
            Так фото будет выглядеть. «Сохранить» кладёт его во временную загрузку: в источники оно уходит только по кнопке «Отправить в …».
          </p>
          <div className="platform-photo-registry__frame">
            <Frame url={adding.previewUrl} alt="Результат: каким получится фото" />
          </div>
          {saving && (
            <p className="platform-photo-registry__meta" role="status">
              {adding.stage}
            </p>
          )}
          <div className="platform-photo-registry__row">
            <Button variant="primary" icon={<Save size={16} aria-hidden="true" />} busy={saving} onClick={actions.saveAdded} actionId="platform.photo-registry.add-save">
              Сохранить
            </Button>
            <Button
              icon={<Pencil size={16} aria-hidden="true" />}
              disabled={saving}
              disabledReason="Идёт сохранение."
              onClick={actions.editBox}
              actionId="platform.photo-registry.add-edit"
            >
              Править рамку
            </Button>
            <Button
              variant="ghost"
              icon={<X size={16} aria-hidden="true" />}
              disabled={saving}
              disabledReason="Идёт сохранение."
              onClick={actions.cancelAdd}
              actionId="platform.photo-registry.add-cancel"
            >
              Отменить
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

/**
 * Окно фото общего товара (`PHOTO-SOURCES-REGISTRY-DESIGN.md` §3.2): слайдер с большим фото, миниатюрами и кнопками «Предыдущее» / «Следующее» (стрелки
 * клавиатуры делают то же), главное фото помечено. Под фото — отметки по источникам из ответа сервера («есть», «нет», «неизвестно») и кнопки
 * «Отправить в …» только у тех, кто принимает фото. Фото, которое пока нигде не лежит, помечено жёлтой плашкой по центру кадра. Отправляет только человек,
 * по кнопке (ADR-23). Ошибки источников и отправки — дословно, с копированием, и не пропадают сами. Жестов нет: всё делается кнопками.
 */
export function PhotoRegistryPanel({ vm, actions }: { vm: PhotoRegistryVm; actions: PhotoRegistryActions }) {
  const input = useRef<HTMLInputElement>(null)
  const [replacing, setReplacing] = useState<{ tile: PhotoTileVm; slot: SlotVm } | null>(null)
  const [confirming, setConfirming] = useState<DeleteConfirm | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const adding = vm.adding !== null
  const wasAdding = useRef(false)

  // Закончили «Добавить фото» — показываем первое фото (новая загрузка стоит первой), а не прежнее.
  useEffect(() => {
    if (wasAdding.current && !adding) setSelectedId(null)
    wasAdding.current = adding
  }, [adding])

  const index = Math.max(
    0,
    vm.tiles.findIndex((item) => item.id === selectedId),
  )
  const current = vm.tiles[index]
  const select = (offset: number) => {
    const next = vm.tiles[index + offset]
    if (next !== undefined) setSelectedId(next.id)
  }

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file !== undefined) actions.addFile(file)
  }

  const onKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft') select(-1)
    else if (event.key === 'ArrowRight') select(1)
  }

  const send = (tile: PhotoTileVm, slot: SlotVm) => {
    if (slot.replaces) setReplacing({ tile, slot })
    else actions.send(tile, slot, false)
  }

  const pair = (item: PairVm) => (
    <li key={item.id} className="platform-photo-registry__pair">
      <div className="platform-photo-registry__pair-frames">
        <Frame url={item.thumbA} alt="Первое из похожих фото" />
        <Frame url={item.thumbB} alt="Второе из похожих фото" />
      </div>
      <p className="platform-photo-registry__meta">{item.because}</p>
      <div className="platform-photo-registry__row">
        <Button onClick={() => actions.decide(item, 'same')} actionId="platform.photo-registry.same">
          Это одно
        </Button>
        <Button onClick={() => actions.decide(item, 'different')} actionId="platform.photo-registry.different">
          Разные
        </Button>
      </div>
    </li>
  )

  const slotRow = (tile: PhotoTileVm, slot: SlotVm) => (
    <li key={slot.key} className="platform-photo-registry__slot">
      <Mark slot={slot} />
      {slot.canAdd && slot.state !== 'present' && (
        <Button
          variant="primary"
          disabled={!slot.canSend}
          disabledReason={slot.blockedReason}
          busy={slot.progress?.phase === 'sending' || slot.progress?.phase === 'running'}
          onClick={() => send(tile, slot)}
          actionId="platform.photo-registry.send"
        >
          {slot.replaces ? `Заменить в «${slot.title}»…` : `Отправить в «${slot.title}»`}
        </Button>
      )}
      {slot.canDelete && (
        <Button
          variant="danger"
          icon={<Trash2 size={16} aria-hidden="true" />}
          disabled={slot.deleteBlockedReason !== null}
          disabledReason={slot.deleteBlockedReason}
          busy={slot.deleteProgress?.phase === 'sending' || slot.deleteProgress?.phase === 'running'}
          onClick={() => setConfirming({ kind: 'source', tile, slot })}
          actionId="platform.photo-registry.delete-source"
        >
          {`Удалить из «${slot.title}»…`}
        </Button>
      )}
      {slot.deleteProgress !== null && (
        <DeleteProgress
          progress={slot.deleteProgress}
          onRetry={() => actions.deleteFromSource(tile, slot)}
          onDismiss={() => actions.dismissDelete(tile, slot)}
        />
      )}
      {slot.limitNote !== null && slot.state !== 'present' && <span className="platform-photo-registry__meta">{slot.limitNote}</span>}
      {slot.progress !== null && slot.progress.error === null && (
        <p className="platform-photo-registry__meta" role="status">
          {slot.progress.text}
        </p>
      )}
      {slot.progress?.error != null && (
        <StatusMessage
          kind="error"
          title={slot.progress.text}
          text={slot.progress.error.message}
          meta={`Код: ${slot.progress.error.code}${slot.progress.error.requestId === null ? '' : ` · запрос ${slot.progress.error.requestId}`}`}
          action={
            <Button variant="ghost" onClick={() => actions.dismissSend(slot)} actionId="platform.photo-registry.send-dismiss">
              Закрыть
            </Button>
          }
        />
      )}
    </li>
  )

  const slider = (tile: PhotoTileVm) => (
    <div
      className="platform-photo-registry__slider"
      onKeyDown={onKey}
      role="group"
      aria-roledescription="слайдер"
      aria-label={`Фото ${String(index + 1)} из ${String(vm.tiles.length)}`}
    >
      <div className="platform-photo-registry__stage">
        <Button
          icon={<ChevronLeft size={20} aria-hidden="true" />}
          aria-label="Предыдущее фото"
          disabled={index === 0}
          disabledReason="Это первое фото."
          onClick={() => select(-1)}
          actionId="platform.photo-registry.prev"
        />
        <div className="platform-photo-registry__frame platform-photo-registry__frame--big">
          <Frame url={tile.thumbUrl} alt={tile.pending ? 'Загруженное фото' : 'Фото товара'} />
          {tile.isMain && (
            <span className="platform-photo-registry__badge">
              <Star size={14} aria-hidden="true" /> Главное
            </span>
          )}
          {tile.pending && (
            <p className="platform-photo-registry__pending" role="note">
              <Upload size={18} aria-hidden="true" /> Пока нигде не лежит
            </p>
          )}
        </div>
        <Button
          icon={<ChevronRight size={20} aria-hidden="true" />}
          aria-label="Следующее фото"
          disabled={index >= vm.tiles.length - 1}
          disabledReason="Это последнее фото."
          onClick={() => select(1)}
          actionId="platform.photo-registry.next"
        />
      </div>
      <p className="platform-photo-registry__meta" aria-live="polite">
        Фото {index + 1} из {vm.tiles.length}
      </p>
      {tile.pending && (
        <div className="platform-photo-registry__row">
          <Button
            variant="danger"
            icon={<Trash2 size={16} aria-hidden="true" />}
            disabled={tile.removal?.phase === 'sending'}
            disabledReason="Удаление уже идёт."
            busy={tile.removal?.phase === 'sending'}
            onClick={() => setConfirming({ kind: 'staging', tile })}
            actionId="platform.photo-registry.remove-staged"
          >
            Убрать загруженное
          </Button>
        </div>
      )}
      {tile.removal !== null && (
        <DeleteProgress
          progress={tile.removal}
          onRetry={() => actions.removeStaged(tile)}
          onDismiss={() => actions.dismissDelete(tile, null)}
        />
      )}
      <ul className="platform-photo-registry__thumbs" aria-label="Миниатюры фото">
        {vm.tiles.map((item, position) => (
          <li key={item.id}>
            <Button
              variant="ghost"
              aria-label={`Показать фото ${String(position + 1)}${item.isMain ? ', главное' : ''}${item.pending ? ', пока нигде не лежит' : ''}`}
              aria-current={item.id === tile.id ? 'true' : undefined}
              data-current={item.id === tile.id ? 'true' : undefined}
              onClick={() => setSelectedId(item.id)}
              actionId="platform.photo-registry.show-photo"
            >
              <Frame url={item.thumbUrl} alt="" />
            </Button>
          </li>
        ))}
      </ul>
      {tile.slots.length === 0 ? (
        <p className="platform-photo-registry__meta">Нет источников с фото, в которые можно отправить.</p>
      ) : (
        <ul className="platform-photo-registry__slots" aria-label="Источники этого фото">
          {tile.slots.map((slot) => slotRow(tile, slot))}
        </ul>
      )}
    </div>
  )

  return (
    <section className="platform-photo-registry" aria-label="Фото товара в источниках">
      <header className="platform-photo-registry__head">
        <h3>Фото в источниках</h3>
        <div className="platform-photo-registry__row">
          <Button icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.photo-registry.reload">
            Обновить
          </Button>
          <Button
            icon={<Upload size={16} aria-hidden="true" />}
            disabled={adding}
            disabledReason="Сначала закончите или отмените добавление предыдущего фото."
            onClick={() => input.current?.click()}
            actionId="platform.photo-registry.upload"
          >
            Добавить фото
          </Button>
          <input ref={input} type="file" accept={ACCEPT} hidden onChange={onFile} data-testid="photo-registry-file" />
        </div>
      </header>

      {vm.problems.map((problem) => (
        <StatusMessage
          key={`${problem.resourceId}|${problem.message}`}
          kind="error"
          title={`${problem.title}: неизвестно, а не «нет»`}
          text={problem.message}
          meta={problem.code === null ? undefined : `Код: ${problem.code}`}
        />
      ))}
      <ActionError error={vm.decision.error} onReload={actions.reload} />
      <ActionError error={vm.sourcesError} onRetry={actions.reload} />

      {vm.adding !== null && <AddPhoto adding={vm.adding} actions={actions} />}

      {vm.adding === null && vm.query.status !== 'ready' && (
        <QueryState
          query={vm.query}
          emptyText="Фотографий этого товара в источниках пока нет. «Добавить фото» кладёт снимок во временную загрузку, откуда его можно отправить в источник."
          onRetry={actions.reload}
        />
      )}

      {vm.adding === null && current !== undefined && slider(current)}

      {vm.adding === null && vm.pairs.length > 0 && (
        <section className="platform-photo-registry__similar" aria-label={`Похожие фото (${String(vm.pairs.length)})`}>
          <h4>Похожие фото ({vm.pairs.length})</h4>
          <ul className="platform-photo-registry__pairs">{vm.pairs.map(pair)}</ul>
        </section>
      )}

      {confirming !== null && (
        <ModalFrame
          title={confirming.kind === 'staging' ? 'Убрать загруженное фото?' : `Удалить фото из «${confirming.slot.title}»?`}
          size="sm"
          covered={false}
          onClose={() => setConfirming(null)}
        >
          <div className="platform-photo-registry__confirm" role="alertdialog" aria-label="Подтверждение удаления фото">
            {confirming.kind === 'staging' ? (
              <>
                <p>Будет удалена временная загрузка: снимок и исходный файл на нашем сервере. В источниках это фото не лежит — там ничего не меняется.</p>
                <p>Вернуть убранное нельзя: фото придётся добавить заново.</p>
              </>
            ) : (
              <>
                <p>Фото будет удалено из карточки товара у источника «{confirming.slot.title}». Это меняет данные источника, а не только наш список.</p>
                <p>
                  {confirming.tile.slots.filter((other) => other.state === 'present').length <= 1
                    ? 'Это единственное место, где фото лежит: после удаления оно пропадёт из списка.'
                    : 'В остальных источниках фото остаётся.'}
                </p>
              </>
            )}
            <div className="platform-photo-registry__row">
              <Button onClick={() => setConfirming(null)} actionId="platform.photo-registry.delete-cancel">
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const target = confirming
                  setConfirming(null)
                  if (target.kind === 'staging') actions.removeStaged(target.tile)
                  else actions.deleteFromSource(target.tile, target.slot)
                }}
                actionId="platform.photo-registry.delete-confirm"
              >
                {confirming.kind === 'staging' ? 'Убрать' : 'Удалить'}
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}

      {replacing !== null && (
        <ModalFrame title={`Заменить картинку в «${replacing.slot.title}»?`} size="sm" covered={false} onClose={() => setReplacing(null)}>
          <div className="platform-photo-registry__confirm" role="alertdialog" aria-label="Подтверждение замены картинки">
            <p>{replacing.slot.limitNote ?? 'Источник держит одну картинку, и она уже стоит.'}</p>
            <p>Отправка заменит стоящую картинку этой фотографией. Прежняя картинка у источника пропадёт.</p>
            <div className="platform-photo-registry__row">
              <Button onClick={() => setReplacing(null)} actionId="platform.photo-registry.replace-cancel">
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const target = replacing
                  setReplacing(null)
                  actions.send(target.tile, target.slot, true)
                }}
                actionId="platform.photo-registry.replace-confirm"
              >
                Заменить
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}
    </section>
  )
}
